import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FooterColumnProps {
  title: string;
  links: Array<{ label: string; to: string; isPlaceholder?: boolean }>;
  isExpanded: boolean;
  onToggle: () => void;
  id: string;
  isMobile: boolean;
}

function FooterColumn({ title, links, isExpanded, onToggle, id, isMobile }: FooterColumnProps) {
  const contentId = `${id}-content`;
  const headerId = `${id}-header`;

  if (isMobile) {
    return (
      <div className="border-b border-gray-800">
        <button
          id={headerId}
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          className="w-full flex items-center justify-between py-4 text-left min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0000FF] focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
        >
          <h3 className="text-base font-semibold text-gray-100">{title}</h3>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
          )}
        </button>
        <div
          id={contentId}
          role="region"
          aria-labelledby={headerId}
          className={`overflow-hidden transition-all duration-200 ${
            isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="pb-4 space-y-2">
            {links.map((link, idx) => (
              <Link
                key={idx}
                to={link.to}
                className={`block text-sm text-gray-300 hover:text-[#0000FF] hover:underline transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0000FF] focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 rounded ${
                  link.isPlaceholder ? 'opacity-60' : ''
                }`}
                onClick={(e) => {
                  if (link.to === '#') {
                    e.preventDefault();
                  }
                }}
              >
                {link.label}
                {link.isPlaceholder && (
                  <span className="sr-only"> (Route not yet implemented)</span>
                )}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-100 mb-2">{title}</h3>
      <nav className="space-y-1">
        {links.map((link, idx) => (
          <Link
            key={idx}
            to={link.to}
            className={`block text-sm text-gray-300 hover:text-[#0000FF] hover:underline transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0000FF] focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 rounded ${
              link.isPlaceholder ? 'opacity-60' : ''
            }`}
            onClick={(e) => {
              if (link.to === '#') {
                e.preventDefault();
              }
            }}
          >
            {link.label}
            {link.isPlaceholder && (
              <span className="sr-only"> (Route not yet implemented)</span>
            )}
          </Link>
        ))}
      </nav>
    </div>
  );
}

export default function Footer() {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const columns = [
    {
      id: 'learn',
      title: 'Learn',
      links: [
        { label: 'Info', to: '/info' },
        { label: 'About', to: '/about' },
        { label: 'FAQs', to: '/faqs' },
      ],
    },
    {
      id: 'buy-domains',
      title: 'Domains',
      links: [
        { label: 'Browse All', to: '/domains' },
        { label: 'Bundles', to: '/bundles' },
      ],
    },
    {
      id: 'lease-finance',
      title: 'Lease',
      links: [
        { label: 'How Leasing Works', to: '/lease' },
        { label: 'Lease-to-Own Terms', to: '#', isPlaceholder: true },
      ],
    },
    {
      id: 'help',
      title: 'Help',
      links: [
        { label: 'Services', to: '/services' },
        { label: 'Support', to: '/support' },
        { label: 'Contact', to: '/contact' },
      ],
    },
    {
      id: 'legal',
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', to: '/privacy' },
        { label: 'Terms of Service', to: '/terms' },
      ],
    },
  ];

  return (
    <footer
      aria-label="Site footer"
      role="contentinfo"
      className="bg-black text-gray-100"
    >
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Visually hidden H2 for screen readers if needed */}
        <h2 className="sr-only">Footer</h2>

        {/* Desktop/Tablet Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          {columns.map((column) => (
            <FooterColumn
              key={column.id}
              id={column.id}
              title={column.title}
              links={column.links}
              isExpanded={false}
              onToggle={() => {}}
              isMobile={false}
            />
          ))}
        </div>

        {/* Mobile Accordions */}
        <div className="md:hidden space-y-0">
          {columns.map((column) => (
            <FooterColumn
              key={column.id}
              id={column.id}
              title={column.title}
              links={column.links}
              isExpanded={expandedSections[column.id] || false}
              onToggle={() => toggleSection(column.id)}
              isMobile={true}
            />
          ))}
        </div>
      </div>
    </footer>
  );
}
